import { useSurfaceProps } from 'features/assetViz/objects/surfaces/util/SurfaceProvider'
import React from 'react'
import {
  GeckoComponentCoords,
  GeckoFixedSensor,
  GeckoMeasurement,
  GeckoMeasurementUnit,
} from 'types'

import { FeatureBox3D } from './procedural/FeatureBox3D'
import { FeatureCylinder3D } from './procedural/FeatureCylinder3D'
import { FeatureLabel3D } from './procedural/FeatureLabel3D'
import { SurfaceFeature3D } from './SurfaceFeature3D'

/**
 * A component representing a fixed sensor (a.k.a. "barnacle") in 3D space
 * @param model             The fixed sensor model (from the asset model)
 * @constructor
 */
export const FixedSensorFeature3D: React.FC<{
  model: GeckoFixedSensor
}> = ({ model }) => {
  //----------------------------------------------------------------------------

  const { bounds } = useSurfaceProps()
  const { labelCoords, positionLabelAbove } = React.useMemo(() => {
    const x = model.geometry.x
    let y = model.geometry.y.plus(
      GeckoMeasurement.CreateFromValue(3, GeckoMeasurementUnit.Inches)
    )
    const positionAbove =
      bounds.y.end.minus(y).num > bounds.height.scale(1 / 10).num
    if (!positionAbove) {
      y = model.geometry.y.minus(
        GeckoMeasurement.CreateFromValue(3, GeckoMeasurementUnit.Inches)
      )
    }
    const labelCoords = GeckoComponentCoords.CreateFromMeasurements(
      bounds.componentID,
      x,
      y
    )
    return { labelCoords, positionLabelAbove: positionAbove }
  }, [model, bounds])

  //----------------------------------------------------------------------------

  return (
    <>
      <FeatureLabel3D
        feature={model}
        coords={labelCoords}
        anchor={positionLabelAbove ? 'bottom' : 'top'}
      />
      <SurfaceFeature3D model={model}>
        <group rotation={[-Math.PI / 2, 0, 0]}>
          {/* Surface box */}
          <FeatureBox3D
            feature={model}
            position={[0, -0.05, 0]}
            width={0.15}
            height={0.05}
            depth={0.1}
            debugColor={'blue'}
          />
          {/* Connector */}
          <group rotation={[Math.PI / 2, 0, 0]}>
            <FeatureCylinder3D
              feature={model}
              position={[0, 0.05, 0]}
              radius={0.02}
              height={0.25}
              debugColor={'purple'}
            />
          </group>
          {/* Body */}
          <FeatureBox3D
            feature={model}
            position={[0, -0.05, 0.3]}
            width={0.15}
            height={0.2}
            depth={0.1}
            color={'rgb(100, 180, 240)'}
            debugColor={'red'}
          />
          {/* Antenna */}
          <FeatureCylinder3D
            feature={model}
            position={[-0.05, 0, 0.25]}
            radius={0.01}
            height={0.3}
            debugColor={'green'}
          />
        </group>
      </SurfaceFeature3D>
    </>
  )
}
